import { LOCALES } from '@bitwize/i18n-messages';
import { Check, Globe } from 'lucide-react';

import {
  LOCALE_META,
  rememberLocale,
  switchLocaleUrl,
  useI18n,
  type AlternatePaths,
} from '../i18n';
import { cn } from '../lib/cn';
import { useDismissibleDetails } from '../lib/useDismissibleDetails';

export interface LanguageSwitcherProps {
  /** The current URL, so the switch lands on the equivalent page. */
  currentUrl: string;
  /**
   * The current page's real path in each locale, when it has one worth
   * knowing about — a detail page whose slug differs by locale supplies
   * this; every other page leaves it unset; and this component does not
   * know or care which case it is in. It has no route knowledge of its own
   * and never fetches anything to get this — the page that already loaded
   * its own data computed it once, and this is a plain lookup into that.
   */
  alternatePaths?: AlternatePaths | undefined;
  className?: string;
}

/**
 * A compact trigger that opens onto the supported locales.
 *
 * `<details>`/`<summary>` rather than a JS-driven open/close: the options
 * below are plain `<a>` links, and this switcher is one of the first things
 * a visitor uses, often before hydration has finished — `<details>` opens on
 * a plain click with no JavaScript at all, so the links stay reachable the
 * whole time. `useDismissibleDetails` layers on the one thing the element
 * doesn't do natively: closing itself on an outside click or Escape.
 *
 * Each option is labelled in its own language — a visitor looking for Arabic
 * is looking for "العربية", not for "Arabic" written in a language they may
 * not read. The trigger itself shows the current locale's code, which is the
 * one place a Latin abbreviation is the right call: it is a compact glance
 * indicator, not a language option.
 */
export function LanguageSwitcher({ currentUrl, alternatePaths, className }: LanguageSwitcherProps) {
  const { locale, t } = useI18n();
  const detailsRef = useDismissibleDetails<HTMLDetailsElement>();

  // Admin routes carry no locale prefix at all — `/admin/messages`, never
  // `/en/admin/messages` — because the admin tree is a client-only SPA with
  // its own routing (see `pages/admin/+config.ts`), not the public site's
  // Server Routing that `switchLocaleUrl` builds URLs for. Running
  // `currentUrl` through it here anyway produced `/ar/admin/messages`: a URL
  // no admin route matches, which crashed the whole shell on load. The
  // language for an admin visitor is decided by the same saved-preference
  // cookie the public site negotiates on, `resolveRequestLocale` reads
  // server-side — so switching it here is `rememberLocale` plus a reload of
  // the *same* URL, not a rewritten one.
  const isAdmin = currentUrl.startsWith('/admin');

  return (
    <details ref={detailsRef} className={cn('relative', className)}>
      <summary
        aria-label={t('common.language.label')}
        title={t('common.language.label')}
        className={cn(
          'flex h-9 list-none items-center gap-1.5 rounded-sm px-2.5 text-sm font-medium text-text-muted',
          'transition-colors duration-[--hs-duration-fast] ease-out hover:bg-surface-hover hover:text-text',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        <Globe className="size-[18px]" aria-hidden="true" />
        <span aria-hidden="true">{locale.toUpperCase()}</span>
      </summary>

      <nav
        aria-label={t('common.language.label')}
        className="absolute end-0 top-[calc(100%+8px)] z-30 flex min-w-36 flex-col gap-0.5 rounded-lg border border-border bg-surface-raised p-1 shadow-md"
      >
        {LOCALES.map((candidate) => {
          const meta = LOCALE_META[candidate];
          const isCurrent = candidate === locale;

          return (
            <a
              key={candidate}
              href={
                isAdmin
                  ? currentUrl
                  : switchLocaleUrl(currentUrl, candidate, alternatePaths?.[candidate])
              }
              hrefLang={candidate}
              lang={candidate}
              dir={meta.dir}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={(event) => {
                if (detailsRef.current) detailsRef.current.open = false;
                if (isAdmin && candidate !== locale) {
                  // A plain link to the same URL does nothing on its own —
                  // the reload is what has to happen, and it has to happen
                  // after the preference is saved, not before.
                  event.preventDefault();
                  rememberLocale(candidate);
                  window.location.assign(currentUrl);
                }
              }}
              className={cn(
                'flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                // `selected`, not `accent-subtle`/`accent`: this component is
                // mounted by both shells, and each marks the current item in
                // its own register (see the token in tokens.css).
                isCurrent
                  ? 'bg-selected font-medium text-selected-fg'
                  : 'text-text-muted hover:bg-surface-hover hover:text-text',
              )}
            >
              {/*
                Native-script name, not "EN"/"AR": a visitor looking for
                Arabic recognises "العربية" on sight, which a Latin
                abbreviation is not.
              */}
              {meta.nativeName}
              {isCurrent ? <Check className="size-4 shrink-0" aria-hidden="true" /> : null}
            </a>
          );
        })}
      </nav>
    </details>
  );
}
